import { useCallback, useEffect, useMemo, useState } from "react";
import { CreditCard, RefreshCw, Webhook, Users, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";

import { api, formatApiError } from "@/api/client";

function cents(c) { return `$${(Number(c || 0) / 100).toFixed(2)}`; }

function Kpi({ label, value, sub, icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-[var(--ds-radius-card)] p-4"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-admin-border)",
      }}
    >
      <div className="flex items-center justify-between">
        <div
          className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
          style={{ color: "var(--ds-color-ink-subtle)" }}
        >
          {label}
        </div>
        {Icon && (
          <Icon
            className="w-4 h-4"
            style={{ color: "var(--ds-color-accent)" }}
            aria-hidden="true"
          />
        )}
      </div>
      <div
        className="mt-2 text-[22px] font-semibold"
        style={{
          color: "var(--ds-color-ink)",
          fontFamily: "var(--ds-font-display)",
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          className="mt-1 text-[11px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

export default function AdminBilling() {
  const [tab, setTab] = useState("subscriptions");
  const [kpi, setKpi] = useState(null);
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (planFilter) params.set("plan_code", planFilter);
      if (statusFilter) params.set("status_filter", statusFilter);
      const [k, s, e] = await Promise.all([
        api.get("/admin/billing"),
        api.get(`/admin/billing/subscriptions?${params.toString()}&limit=100`),
        api.get("/admin/billing/webhook-events?limit=100"),
      ]);
      setKpi(k.data?.kpis || {});
      setSubs(s.data?.items || []);
      setEvents(e.data?.items || []);
    } catch (ex) { toast.error(formatApiError(ex)); }
    finally { setLoading(false); }
  }, [planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout
      title="Billing"
      subtitle="Stripe subscriptions, MRR, and webhook activity"
      actions={
        <button
          data-testid="billing-refresh"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-[13px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      }
    >
      <div className="mb-4 max-w-3xl">
        <AdminPageHelp
          title="Billing"
          purpose="Overall Stripe billing health — subscriptions, MRR, and webhook activity."
          configures="Nothing — read-only. Filter by plan, status, and time window."
          impact="No user-facing impact. Diagnose subscription state issues and webhook failures."
          safety="Read-only. Stripe remains the source of truth."
        />
      </div>
      <div data-testid="billing-kpis" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi testid="kpi-active-paying" label="Active paying subs" value={kpi?.active_paying_subscriptions ?? "—"} icon={Users} />
        <Kpi testid="kpi-mrr" label="MRR" value={kpi?.mrr_cents != null ? cents(kpi.mrr_cents) : "—"} sub="Sum of monthly prices × active subs" icon={CreditCard} />
        <Kpi testid="kpi-free" label="Free subs" value={kpi?.free_subscriptions ?? "—"} icon={Users} />
        <Kpi testid="kpi-canceling" label="Canceling at period end" value={kpi?.canceling_at_period_end ?? "—"} icon={AlertCircle} />
      </div>

      <div className="flex items-center border-b border-[var(--ds-color-admin-border)] mb-5">
        <button data-testid="billing-tab-subs" onClick={() => setTab("subscriptions")} className={`px-4 py-2 text-[13px] ${tab === "subscriptions" ? "text-[var(--ds-color-ink)]" : "text-[var(--ds-color-ink-muted)]"}`} style={{ borderBottom: tab === "subscriptions" ? "2px solid var(--ds-color-accent)" : "2px solid transparent" }}>
          Subscriptions <span className="ml-1 text-[10px] text-[var(--ds-color-ink-muted)]">({subs.length})</span>
        </button>
        <button data-testid="billing-tab-webhooks" onClick={() => setTab("webhooks")} className={`px-4 py-2 text-[13px] ${tab === "webhooks" ? "text-[var(--ds-color-ink)]" : "text-[var(--ds-color-ink-muted)]"}`} style={{ borderBottom: tab === "webhooks" ? "2px solid var(--ds-color-accent)" : "2px solid transparent" }}>
          Webhook events <span className="ml-1 text-[10px] text-[var(--ds-color-ink-muted)]">({events.length})</span>
        </button>
      </div>

      {tab === "subscriptions" ? (
        <>
          <div className="flex gap-2 mb-3">
            <select data-testid="billing-filter-plan" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[12px] text-[var(--ds-color-ink)]" style={{ background: "var(--ds-color-surface)" }}>
              <option value="">All plans</option>
              <option value="free">Free</option>
              <option value="member">Member</option>
              <option value="premium">Premium</option>
            </select>
            <select data-testid="billing-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[12px] text-[var(--ds-color-ink)]" style={{ background: "var(--ds-color-surface)" }}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="canceled">Canceled</option>
              <option value="past_due">Past due</option>
            </select>
          </div>
          <div data-testid="billing-subs-table" className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] overflow-hidden" style={{ background: "var(--ds-color-surface)" }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Stripe sub</th>
                  <th className="px-4 py-3 font-medium">Cancel @ end</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {subs.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[var(--ds-color-ink-muted)]">No subscriptions match.</td></tr>
                )}
                {subs.map(s => (
                  <tr key={s.id} data-testid={`billing-sub-row-${s.id}`} className="border-t border-[var(--ds-color-admin-border)]">
                    <td className="px-4 py-3 text-[var(--ds-color-ink)] text-xs">{s.user_email || s.user_id}</td>
                    <td className="px-4 py-3 text-[var(--ds-color-ink-muted)]">{s.plan_code}</td>
                    <td className="px-4 py-3"><span className="text-xs" style={{ color: s.status === "active" ? "var(--ds-color-success)" : "var(--ds-color-danger)" }}>{s.status}</span></td>
                    <td className="px-4 py-3 font-mono text-[10px] text-[var(--ds-color-ink-muted)]">{s.stripe_subscription_id || (s.is_stub ? "stub" : "—")}</td>
                    <td className="px-4 py-3 text-xs">{s.cancel_at_period_end ? <span className="text-[var(--ds-color-warning)]">Yes</span> : <span className="text-[var(--ds-color-ink-muted)]">No</span>}</td>
                    <td className="px-4 py-3 text-[var(--ds-color-ink-muted)] text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div data-testid="billing-webhooks-table" className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] overflow-hidden" style={{ background: "var(--ds-color-surface)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
                <th className="px-4 py-3 font-medium">Event ID</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Processed</th>
                <th className="px-4 py-3 font-medium">Mode</th>
                <th className="px-4 py-3 font-medium">Received</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && !loading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[var(--ds-color-ink-muted)]">No webhook events received yet.</td></tr>
              )}
              {events.map(e => (
                <tr key={e.id} data-testid={`billing-event-row-${e.id}`} className="border-t border-[var(--ds-color-admin-border)]">
                  <td className="px-4 py-3 font-mono text-[10px] text-[var(--ds-color-ink)]">{e.stripe_event_id}</td>
                  <td className="px-4 py-3 text-[var(--ds-color-ink-muted)]">{e.event_type}</td>
                  <td className="px-4 py-3">{e.processed ? <CheckCircle2 className="w-3.5 h-3.5 text-[var(--ds-color-success)]" /> : <span className="text-xs text-[var(--ds-color-danger)]">{e.error ? "Failed" : "Pending"}</span>}</td>
                  <td className="px-4 py-3 text-xs text-[var(--ds-color-ink-muted)]">{e.is_stub ? "stub" : "live"}</td>
                  <td className="px-4 py-3 text-[var(--ds-color-ink-muted)] text-xs">{e.received_at ? new Date(e.received_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
